import { useState } from 'react';
import axios from 'axios';
import { AlertCircle, Calendar, Loader2, X } from 'lucide-react';

const API_URL = process.env.REACT_APP_API_URL;

function formatDateForDisplay(isoDate) {
  if (!isoDate) return '';
  const [year, month, day] = isoDate.split('T')[0].split('-');
  return `${day}/${month}/${year}`;
}

export default function PostponePackageModal({ packageItem, userId, onClose, onSuccess }) {
  const [selectedDate, setSelectedDate] = useState('');
  const [reason, setReason] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();

    if (!selectedDate) {
      alert('Por favor selecciona una fecha');
      return;
    }

    setIsLoading(true);

    try {
      await axios.patch(`${API_URL}/api/packages/in-fastmax/${packageItem.id}/postpone`, {
        user_id: userId,
        delivery_date: selectedDate,
        postponed_reason: reason.trim() || null,
      });

      alert(`Entrega #${packageItem.delivery_point_id} pospuesta para el ${formatDateForDisplay(selectedDate)}`);
      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Error postponing package:', error);
      const errorMsg = error.response?.data?.error || 'Error al posponer entrega';
      alert(errorMsg);
    } finally {
      setIsLoading(false);
    }
  }

  if (!packageItem) return null;

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center bg-black/50 p-4">
      <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
        <div className="flex items-center justify-between bg-[#B1336E] px-5 py-4">
          <h3 className="flex items-center gap-2 text-lg font-bold text-white font-montserrat">
            <Calendar className="h-5 w-5" />
            Posponer entrega
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg p-2 text-white transition-colors hover:bg-white/20"
            title="Cerrar"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4 p-5">
          <div className="space-y-1 rounded-xl border border-slate-200 bg-slate-50 p-4">
            <p className="text-sm text-slate-700 font-montserrat">
              <span className="font-semibold">Entrega:</span> #{packageItem.delivery_point_id}
            </p>
            {packageItem.recipient_name && (
              <p className="text-sm text-slate-700 font-montserrat">
                <span className="font-semibold">Destinatario:</span> {packageItem.recipient_name}
              </p>
            )}
            {packageItem.district && (
              <p className="text-sm text-slate-700 font-montserrat">
                <span className="font-semibold">Distrito:</span> {packageItem.district}
              </p>
            )}
            <p className="text-sm text-slate-700 font-montserrat">
              <span className="font-semibold">Fecha actual:</span>{' '}
              {packageItem.delivery_date ? formatDateForDisplay(packageItem.delivery_date) : 'N/A'}
            </p>
            <p className="truncate text-xs text-slate-400 font-mono">
              {packageItem.tracking_code}
            </p>
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700 font-montserrat">
              Nueva fecha de entrega
            </label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="w-full rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-800 outline-none transition-all focus:border-[#B1336E] focus:ring-2 focus:ring-[#B1336E]/20 font-montserrat"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-semibold text-slate-700 font-montserrat">
              Motivo de postergacion <span className="font-normal text-slate-400">(opcional)</span>
            </label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ej: Cliente solicito cambio de fecha"
              rows={3}
              className="w-full resize-none rounded-xl border border-slate-300 px-3 py-2 text-sm text-slate-700 outline-none transition-all placeholder:text-slate-400 focus:border-[#B1336E] focus:ring-2 focus:ring-[#B1336E]/20 font-montserrat"
            />
          </div>

          <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 p-3">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <p className="text-xs leading-relaxed text-amber-800 font-montserrat">
              Se actualizara la fecha y el estado a <strong>postergado</strong>. La fecha original quedara registrada automaticamente.
            </p>
          </div>

          <div className="flex gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 rounded-xl bg-slate-200 px-4 py-2 text-sm font-semibold text-slate-700 transition-colors hover:bg-slate-300 font-montserrat"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isLoading || !selectedDate}
              className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-[#B1336E] px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-[#B1336E]/90 disabled:cursor-not-allowed disabled:bg-slate-300 font-montserrat"
            >
              {isLoading && <Loader2 className="h-4 w-4 animate-spin" />}
              {isLoading ? 'Posponiendo...' : 'Posponer'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
